import React, { useReducer, useEffect, useCallback } from "react";
import { funcReducer } from '../../../../utils/defaultReducer.js'
import api from "../../../../api";

import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Divider,
  Box,
} from "@mui/material";
import QRCode from "react-qr-code";

import LoadingButton from "@mui/lab/LoadingButton";
import Loading from "../../../Loading/index.js";
import useNotification from "../../../Notification";
import ScoutSocketQRComponent from "../SocketQRComponent/index.js";

import CloseIcon from "@mui/icons-material/Close";

let pollingId = 0

const getPairingCodeFromResponse = (data = {}) => (
  data.pairingCode ||
  data.PairingCode ||
  data.qrcode?.pairingCode ||
  data.qrCode?.pairingCode ||
  ''
);

const getQrCodeFromResponse = (data = {}) => (
  data.code ||
  data.Code ||
  data.qrcode?.code ||
  data.qrCode?.code ||
  ''
);

const formatPairingCode = (code = '') => (
  String(code)
    .replace(/[\s-]/g, '')
    .replace(/(.{4})(?=.)/g, '$1-')
);

export default function ConnectModal({ account, enterpriseId, handleClose }) {

  const [state, setState] = useReducer(funcReducer, {
    qrCode: null,
    pairingCode: '',
    pairingError: '',
    loading: true,
    socket: null,
  });

  const getQR = useCallback(async ({ showLoading = true } = {}) => {
    if (showLoading) {
      setState({ loading: true });
    }
    try {
      const res = await api.get(
        `api/enterprises/${enterpriseId}/scout_accounts/${account.id}/getqr/`,
        { cache: 'no-store' }
      );
      const qrCode = getQrCodeFromResponse(res.data);
      const pairingCode = getPairingCodeFromResponse(res.data);

      if (res.status === 200) {
        setState((current) => ({
          ...current,
          qrCode: qrCode || current.qrCode,
          pairingCode: pairingCode || current.pairingCode,
          pairingError: pairingCode || current.pairingCode
            ? ''
            : 'Não conseguimos gerar o código para conectar. Por favor, tente novamente em alguns instantes.',
        }));
      } else if (showLoading) {
        console.warn('[Scout pairing][legacy modal] getqr returned non-200 status', res);
        setState({ pairingError: 'Não foi possível gerar o código de pareamento.' });
        useNotification(
          "Erro!",
          `Erro ao gerar código QR.`,
          "danger"
        );
      }
    } catch (error) {
      if (showLoading) {
        console.error('[Scout pairing][legacy modal] error generating QR/pairing code', error);
        setState({ pairingError: 'Não conseguimos gerar o código para conectar. Por favor, tente novamente em alguns instantes.' });
        useNotification(
          "Erro!",
          `Erro ao gerar código QR.`,
          "danger"
        );
      }
    } finally {
      if (showLoading) {
        setState({ loading: false });
      }
    }
  });

  const copyPairingCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(String(state.pairingCode).replace(/[\s-]/g, ''));
      useNotification("Sucesso!", "Código de pareamento copiado.", "success");
    } catch (error) {
      console.error("Error copying pairing code", error);
      useNotification("Erro!", "Não foi possível copiar o código.", "danger");
    }
  }, [state.pairingCode]);

  const checkConnection = useCallback(async () => {
    const res = await api.get(
      `api/enterprises/${enterpriseId}/scout_accounts/${account.id}/checkconnection/`
    );
    if (res.status === 200) {
      if (res.data.state == "open") {
        clearInterval(pollingId);
        handleClose();
        return true;
      } else if (res.data.state == "wrongNumber") {
        clearInterval(pollingId);
        useNotification(
          "Erro!",
          "Número errado para conectar.",
          "danger");
        handleClose();
        return true;
      }
      return false;
    } else {
      console.error("Error checking connection", res);
      handleClose();
      return true;
    }
  });

  useEffect(() => {
    getQR();
    clearInterval(pollingId);
    pollingId = setInterval(async () => {
      const connectionResolved = await checkConnection();
      if (!connectionResolved) {
        await getQR({ showLoading: false });
      }
    }, 5 * 1000);

    return () => {
      // Cleanup on component unmount
      clearInterval(pollingId);
    };
  }, [account]);

  return (
    <div>
      <ScoutSocketQRComponent account={account} parent={state} setParent={setState}/>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={true}
        disablePortal
        disableEnforceFocus
        maxWidth='xs'
        sx={{ position: "absolute" }}
      >
        <Loading loading={state.loading} />
        <DialogTitle className="group-flexbox">
          Conectar com o Scout
          <Button sx={{ minWidth: "2rem", padding: "0" }} onClick={handleClose}>
            <CloseIcon />
          </Button>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {state.qrCode && <QRCode value={state.qrCode} />}

            <Box sx={{ mt: 2, textAlign: 'center', width: '100%' }}>
              <DialogContentText sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                Código de pareamento
              </DialogContentText>
              {state.pairingCode ? (
                <>
                  <DialogContentText sx={{ fontFamily: 'monospace', fontSize: '1.35rem', fontWeight: 700, letterSpacing: '0.14em', color: '#111827', mb: 1 }}>
                    {formatPairingCode(state.pairingCode)}
                  </DialogContentText>
                  <DialogContentText sx={{ fontSize: '0.78rem', mb: 1.5 }}>
                    No WhatsApp, escolha conectar com número de telefone e informe este código.
                  </DialogContentText>
                  <Button variant="outlined" size="small" onClick={copyPairingCode}>
                    Copiar código
                  </Button>
                </>
              ) : (
                <>
                  <DialogContentText sx={{ fontSize: '0.78rem', mb: 1 }}>
                    Você também pode conectar pelo número de telefone. O código aparecerá aqui.
                  </DialogContentText>
                  {state.pairingError && (
                    <DialogContentText sx={{ fontSize: '0.78rem', color: '#dc2626' }}>
                      {state.pairingError}
                    </DialogContentText>
                  )}
                </>
              )}
            </Box>
          </Box>

        </DialogContent>
        <DialogActions>
          <Box className="user-footer">
            <LoadingButton className="button-created" onClick={handleClose}>
              Cancelar
            </LoadingButton>
            <LoadingButton
              className="button-created"
              onClick={() => submit(name, number)}
              disabled={!name || !number}
            >
              Salvar
            </LoadingButton>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
}
